import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { formatMoney } from "../../../utils/formatMoney";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import colors from "@styling/colors";
import type { Money } from "@KassaroAB/shared/types/money";

const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${spacing.x05}px;
  white-space: nowrap;
`;

const Price = styled.div`
  font-size: ${fontSizes.regular}px;
  font-weight: 500;
  color: ${colors.text};
`;

const PriceMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  font-size: ${fontSizes.small}px;
`;

const OriginalPrice = styled.span`
  color: ${colors.textMuted};
  text-decoration: line-through;
`;

const DiscountNote = styled.span`
  color: ${colors.success};
  font-weight: 500;
`;

interface BillingItemMonthlyPriceProps {
  unitPrice: Money | null | undefined;
  quantity?: number;
  discount?: Money | null;
}

export const BillingItemMonthlyPrice = ({
  unitPrice,
  quantity = 1,
  discount,
}: BillingItemMonthlyPriceProps) => {
  const { t } = useTranslation();
  if (!unitPrice) {
    return null;
  }

  const grossAmount = unitPrice.amount * quantity;
  const discountAmount = Math.min(discount?.amount ?? 0, grossAmount);
  const netAmount = Math.max(0, grossAmount - discountAmount);
  const currency = unitPrice.currency;
  const netPrice = { amount: netAmount, currency };

  if (discountAmount <= 0) {
    return (
      <Price>
        {formatMoney(netPrice)}
        {t("packageSelection.perMonth")}
      </Price>
    );
  }

  return (
    <PriceBlock>
      <Price>
        {formatMoney(netPrice)}
        {t("packageSelection.perMonth")}
      </Price>
      <PriceMeta>
        <OriginalPrice>
          {formatMoney({ amount: grossAmount, currency })}
        </OriginalPrice>
        <DiscountNote>
          {t("packageSelection.discountOff", {
            amount: formatMoney({ amount: discountAmount, currency }),
          })}
        </DiscountNote>
      </PriceMeta>
    </PriceBlock>
  );
};
